// Copyright (c) Docugami, Inc. All rights reserved.

import { ICommandBarItemProps, TooltipDelay, DirectionalHint, CommandBar } from "@fluentui/react";
import { useState } from "react";
import { toPascalCase } from "utils/StringUtils";
import { CommandButton, ICommandButton } from "./CommandButton";
import { ShowOnSlotHoverClassName } from "./ItemSlot";
import { appContentColor } from "components/shell/presentation/AppStyles";

interface ItemSlotCommandsProps {
    showOnHover?: boolean;
    commands?: ICommandButton[];
    overflowCommands?: ICommandButton[];
    showItemsToRight?: boolean;
}

export const ItemSlotCommandBar = (props: ItemSlotCommandsProps): JSX.Element | null => {
    const [isOpen, setIsOpen] = useState(false);

    if (!props.commands) {
        return null;
    }


    const commandButtonToCommandBarItem = (commandButton: ICommandButton) => {
        return ({
            key: commandButton.label,
            text: commandButton.label,
            tooltipHostProps: {
                content: commandButton.label,
                delay: TooltipDelay.zero,
                directionalHint: DirectionalHint.topCenter
            },
            onClick: commandButton.onClick,
            disabled: !commandButton.canExecute,
            // this needs to be set so that `onRenderIcon` is called for overflow menu items
            iconProps: { iconName: '' },
            onRenderIcon: () => (
                <CommandButton
                    dataAutomationId={toPascalCase(commandButton.label, '')}
                    key={`${commandButton.label}_command `}
                    command={{ ...commandButton, showLabel: false }} />
            ),
            // TODO: we should add an onRender to handle rendering menu items only (renderedInOverflow)
            // the main issue is that the 'icon' should not be a 'button', since the menu item itself is a button
            // (i.e. DgElementButton is used in CommandButton - and this throws off the queries fluent uses for keyboard navigation)
            // might also cause other issues, since buttons should not be nested.
            iconOnly: true,
            itemProps: {
                styles: {
                    root: {
                        padding: '0 8px'
                    },
                    linkContent: {
                        gap: '5px'
                    }
                }
            }
        })
    }

    const items = props.commands?.map<ICommandBarItemProps>(commandButtonToCommandBarItem);
    const overflowItems = props.overflowCommands?.map<ICommandBarItemProps>(commandButtonToCommandBarItem);

    const styles = {
        root: {
            height: 'unset',
            padding: 0,
            margin: '5px',
            ".ms-Button--commandBar": {
                minWidth: 'unset',
                ".ms-Icon": {
                    fontWeight: 600,
                    color: appContentColor
                }
            }
        }
    }

    return (
        <CommandBar
            className={!isOpen && props.showOnHover ? ShowOnSlotHoverClassName : ''}
            items={props.showItemsToRight ? [] : items}
            farItems={props.showItemsToRight ? items : []}
            overflowItems={overflowItems}
            styles={styles}
            overflowButtonProps={{
                onMenuClick: () => setIsOpen(true),
                onAfterMenuDismiss: () => setIsOpen(false),
                styles: {
                    root: {
                        width: 20
                    }
                },
                menuIconProps: {
                    iconName: 'MoreVertical'
                },
                menuProps: {
                    items: [],
                    styles: { root: { minWidth: '100px' } },
                    directionalHint: DirectionalHint.bottomRightEdge
                },
            }} />
    )
}
